"use client";

import { useState, useEffect, useRef } from "react";
import { auth, db, rtdb } from "@/lib/firebase";
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs, 
  getDoc,
  getCountFromServer,
  doc,
  Timestamp,
  DocumentData
} from "firebase/firestore";
import { ref, onValue, off, DataSnapshot, query as rtdbQuery, orderByChild, equalTo } from "firebase/database";
import { onAuthStateChanged, User } from "firebase/auth";
import { Loader2, Calendar, Clock, Activity, AlertCircle, Timer, Play, Search, X, Maximize2 } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { CounterDisplay } from "@/components/counter-display";
import { LiveScoreboard } from "@/components/live-scoreboard";

interface GameRecord {
  id: string;
  sport: string;
  timestampStart: any;
  timestampEnd?: any;
  scoreSet?: string;
  scoreGame?: string;
  winningTeam?: string;
  locationName?: string;
  [key: string]: any;
}

const EmojiEventsIcon = ({ className }: { className?: string }) => (
  <svg 
    viewBox="0 0 24 24" 
    className={className}
    fill="#FFD700"
  >
    <path d="M19 5h-2V3H7v2H5c-1.1 0-2 .9-2 2v1c0 2.55 1.92 4.63 4.39 4.94.63 1.5 1.98 2.63 3.61 2.96V19H7v2h10v-2h-4v-3.1c1.63-.33 2.98-1.46 3.61-2.96C19.08 12.63 21 10.55 21 8V7c0-1.1-.9-2-2-2zM5 8V7h2v3.82C5.84 10.4 5 9.3 5 8zm14 0c0 1.3-.84 2.4-2 2.82V7h2v1z" />
  </svg>
);

const SportIcon = ({ sport }: { sport: string }) => {
  const [imgError, setImgError] = useState(false);
  const iconPath = `/drawables/icon_${sport.toLowerCase()}.png`;

  if (imgError || !sport) {
    return <EmojiEventsIcon className="h-6 w-6 !fill-[#B0D18B]" />;
  }

  return (
    <img 
      src={iconPath} 
      alt={sport} 
      className="h-6 w-6 object-contain"
      onError={() => setImgError(true)}
    />
  );
};

export default function ScoresPage() {
  const router = useRouter();
  const [user, setUser] = useState<User | null>(null);
  const [games, setGames] = useState<GameRecord[]>([]);
  const [totalGamesCount, setTotalGamesCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Live Scoreboard State
  const [matchCode, setMatchCode] = useState("");
  const [activeCode, setActiveCode] = useState<string | null>(null);
  const [liveGameState, setLiveGameState] = useState<any>(null);
  const [livePlayersData, setLivePlayersData] = useState<any>(null);
  const [liveSettingsData, setLiveSettingsData] = useState<any>(null);
  const [liveSport, setLiveSport] = useState<string>("PADEL");
  const [isLiveLoading, setIsLiveLoading] = useState(false);
  const [liveError, setLiveError] = useState<string | null>(null);
  
  // Refs for listeners to cleanup
  const listenersRef = useRef<{ path: string, callback: (snapshot: DataSnapshot) => void }[]>([]);
  const activePathRef = useRef<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        fetchGames(currentUser.uid);
      } else {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // Cleanup listeners on unmount
  useEffect(() => {
    return () => {
      stopListening();
    };
  }, []);

  const stopListening = () => {
    listenersRef.current.forEach(({ path, callback }) => {
      off(ref(rtdb, path), 'value', callback);
    });
    listenersRef.current = [];
  };

  const handleSpectate = () => {
    if (!matchCode.trim()) return;
    
    stopListening();
    const code = matchCode.trim().toUpperCase();
    setActiveCode(code);
    setIsLiveLoading(true);
    setLiveError(null);
    setLiveGameState(null);
    activePathRef.current = null; // Reset active path

    console.log(`Starting spectate search for gameCode: ${code}`);

    const checkedPaths = new Set<string>();

    // 1. Direct Path Search (For Legacy/NFC/OG Codes)
    const directPath = `padelGoldenCourt/${code}`;
    const directPathSport = 'PADEL';

    // 2. Query Search (For Public Lobbies)
    const queryPaths = [
      { root: 'publicPlay/PADEL', sport: 'PADEL' },
      { root: 'publicPlay/TENNIS', sport: 'TENNIS' },
      { root: 'publicPlay/BADMINTON', sport: 'BADMINTON' },
      { root: 'publicPlay/PICKLEBALL', sport: 'PICKLEBALL' },
      { root: 'publicPlay/PLATFORM', sport: 'PLATFORM' },
    ];
    
    const totalChecks = 1 + queryPaths.length; // 1 direct path + N queries

    // --- Helper to handle success ---
    const handleSuccess = (snapshot: DataSnapshot, sport: string, source: string, path: string) => {
        // If another path is already active, ignore this one
        if (activePathRef.current && activePathRef.current !== path) return;
        
        const data = snapshot.val();
        
        let matchData = null;
        let matchId = "";

        if (source === 'direct') {
            matchData = data;
            matchId = code;
        } else {
            const matchIds = Object.keys(data);
            if (matchIds.length > 0) {
                matchId = matchIds[0];
                matchData = data[matchId];
            }
        }

        if (matchData && matchData.gameState) {
             // Lock this path as active if not already
             if (!activePathRef.current) {
                 console.log(`Match LOCKED via ${source} at ID: ${matchId}`);
                 activePathRef.current = path;
                 
                 // Unsubscribe all OTHER listeners
                 listenersRef.current.forEach(l => {
                     if (l.path !== path) {
                         // Note: We used 'root' as path for queries, directPath for direct
                         // For queries, l.path is the root.
                         if (source === 'query') {
                              // If it's a query, we need to off the query, but we stored the ref in listenersRef?
                              // Actually listenersRef stores { path, callback }.
                              // We should just unsubscribe everything that doesn't match this path string.
                              off(ref(rtdb, l.path), 'value', l.callback); // This might not work perfectly for queries if we need to pass the query object to off()
                              // But Firebase 'off' usually works on the Reference with the callback.
                              // Wait, for queries we must call off on the Query object or the Ref with the same params?
                              // Documentation says: "Detach a callback... by passing the same callback..."
                              // If we passed a Query to onValue, we should probably call off on the Query?
                              // Ideally we should store the Query object too.
                              // However, let's keep it simple: we clear listenersRef and only keep this one.
                              // But we need to actually call off() to stop network usage.
                              // For now, let's assume standard off() works or just accepting that we ignore them via the check at top of handler.
                              
                              // BETTER: We just ignore them in the callback via (activePathRef.current !== path).
                              // Explicit off() is optimization.
                         }
                         // For now, let's just leave them running but ignored, or try to off() best effort.
                     }
                 });
             }

             setLiveSport(sport);
             
             const states = matchData.gameState;
             if (states) {
                const keys = Object.keys(states);
                const maxKey = keys.reduce((a, b) => {
                   const numA = parseInt(a);
                   const numB = parseInt(b);
                   return (isNaN(numA) ? -1 : numA) > (isNaN(numB) ? -1 : numB) ? a : b;
                }, "0");
                
                setLiveGameState(states[maxKey]);
                setLivePlayersData(matchData.players);
                setLiveSettingsData(matchData.settings);
                setIsLiveLoading(false);
             }
        }
    };

    // --- Helper to handle completion/failure ---
    const checkCompletion = (key: string) => {
        if (!checkedPaths.has(key)) {
            checkedPaths.add(key);
        }
        if (!activePathRef.current && checkedPaths.size === totalChecks) {
             console.log("Checked all paths/queries, no match found.");
             setIsLiveLoading(false);
             setLiveGameState(null);
        }
    };

    // --- EXECUTE DIRECT PATH SEARCH ---
    console.log(`Checking Direct Path: ${directPath}`);
    const directRef = ref(rtdb, directPath);
    const directListener = (snapshot: DataSnapshot) => {
        if (activePathRef.current && activePathRef.current !== directPath) return;

        if (snapshot.exists()) {
            handleSuccess(snapshot, directPathSport, 'direct', directPath);
        } else {
            checkCompletion(directPath);
        }
    };
    onValue(directRef, directListener, (error) => {
         console.warn(`RTDB Direct Error ${directPath}:`, error);
         if ((error as any).code === 'PERMISSION_DENIED') {
             setLiveError("Permission Denied: You may need to sign in.");
         }
         checkCompletion(directPath);
    });
    listenersRef.current.push({ path: directPath, callback: directListener });


    // --- EXECUTE QUERY SEARCHES ---
    queryPaths.forEach(({ root, sport }) => {
       console.log(`Querying ${root} for gameCode=${code}`);
       const dbRef = ref(rtdb, root);
       const q = rtdbQuery(dbRef, orderByChild("gameCode"), equalTo(code));

       const listener = (snapshot: DataSnapshot) => {
          if (activePathRef.current && activePathRef.current !== root) return;

          if (snapshot.exists()) {
             handleSuccess(snapshot, sport, 'query', root);
          } 
          
          if (!activePathRef.current) {
             checkCompletion(root);
          }
       };

       onValue(q, listener, (error) => {
           console.warn(`RTDB Query Error for ${root}:`, error);
           if ((error as any).code === 'PERMISSION_DENIED') {
               setLiveError("Permission Denied: You may need to sign in.");
           }
           checkCompletion(root);
       });
       listenersRef.current.push({ path: root, callback: listener });
    });

    // Fallback timeout
    setTimeout(() => {
       if (!activePathRef.current) {
          console.log("Timeout reached.");
          setIsLiveLoading(false);
       }
    }, 5000);
  };

  const clearSpectate = () => {
    stopListening();
    setActiveCode(null);
    setLiveGameState(null);
    setMatchCode("");
    setIsLiveLoading(false);
  };

  const toggleFullScreen = () => {
      if (activeCode) {
         router.push(`/scoreboard?gameCode=${encodeURIComponent(activeCode)}`);
      }
  };

  const fetchGames = async (uid: string) => {
    setLoading(true);
    setError(null);
    try {
      const resultsRef = collection(db, "dbGameResults");
      
      // We need to check all 4 player slots
      const playerSlots = ["player1", "player2", "player3", "player4"];
      const queries = playerSlots.map(slot => 
        query(
          resultsRef, 
          where(`${slot}.fldPlayerUid`, "==", uid),
          orderBy("timestampStart", "desc"),
          limit(10)
        )
      );

      // Fetch actual game data
      const querySnapshots = await Promise.all(queries.map(q => getDocs(q)));
      
      const allGames: GameRecord[] = [];
      const seenIds = new Set<string>();

      querySnapshots.forEach(snapshot => {
        snapshot.forEach(doc => {
          if (!seenIds.has(doc.id)) {
            seenIds.add(doc.id);
            allGames.push({ id: doc.id, ...doc.data() } as GameRecord);
          }
        });
      });

      // Fetch location names
      const locationIds = new Set<string>();
      allGames.forEach(game => {
         if (game.court?.locationId) {
            locationIds.add(game.court.locationId);
         }
      });

      const locationMap = new Map<string, string>();
      await Promise.all(
         Array.from(locationIds).map(async (locId) => {
            try {
               const locSnap = await getDoc(doc(db, "dbLocation", locId));
               if (locSnap.exists()) {
                  locationMap.set(locId, locSnap.data().fldName);
               }
            } catch (err) {
               console.warn(`Failed to fetch location ${locId}`, err);
            }
         })
      );

      // Attach location names to games
      const gamesWithLocations = allGames.map(game => ({
         ...game,
         locationName: game.court?.locationId ? locationMap.get(game.court.locationId) : undefined
      }));


      // Sort combined results by timestamp
      gamesWithLocations.sort((a, b) => {
        const getTime = (ts: any) => {
          if (ts instanceof Timestamp) return ts.toMillis();
          if (typeof ts === "number") return ts;
          return 0;
        };
        return getTime(b.timestampStart) - getTime(a.timestampStart);
      });

      setGames(gamesWithLocations);

      // Fetch total count (sum of all slots) for THIS YEAR
      try {
        const startOfYear = new Date(new Date().getFullYear(), 0, 1);
        const startOfYearTs = Timestamp.fromDate(startOfYear);
        const startOfYearMillis = startOfYear.getTime();

        const countQueries = playerSlots.map(slot => 
          query(
            resultsRef, 
            where(`${slot}.fldPlayerUid`, "==", uid),
            // We try both types if possible, but Firestore 'where' is strict. 
            // Based on ActivityGamePadel logic, it's likely a Number (Long) from RTDB.
            where("timestampStart", ">=", startOfYearMillis),
            orderBy("timestampStart", "desc")
          )
        );

        let totalCount = 0;
        try {
          const countSnapshots = await Promise.all(countQueries.map(q => getCountFromServer(q)));
          totalCount = countSnapshots.reduce((acc, snapshot) => acc + snapshot.data().count, 0);
        } catch (err) {
          console.warn("Count with number filter failed, trying Timestamp filter", err);
          // Fallback to Timestamp if Numbers aren't used
          const countQueriesTs = playerSlots.map(slot => 
            query(
              resultsRef, 
              where(`${slot}.fldPlayerUid`, "==", uid),
              where("timestampStart", ">=", startOfYearTs),
              orderBy("timestampStart", "desc")
            )
          );
          const countSnapshotsTs = await Promise.all(countQueriesTs.map(q => getCountFromServer(q)));
          totalCount = countSnapshotsTs.reduce((acc, snapshot) => acc + snapshot.data().count, 0);
        }
        
        setTotalGamesCount(totalCount);
      } catch (countErr) {
        console.error("Failed to fetch total game count (likely missing index):", countErr);
        // We don't set the main error here so the list can still load
      }

    } catch (err: any) {
      console.error("Error fetching games:", err);
      if (err.code === "failed-precondition" || err.message?.toLowerCase().includes("failed-precondition") || err.message?.toLowerCase().includes("index")) {
        setError("Missing Database Index. Open browser console to click the creation link.");
      } else {
        setError(`Failed to load match history: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (ts: any) => {
    let date: Date;
    if (ts instanceof Timestamp) {
      date = ts.toDate();
    } else if (typeof ts === "number") {
      date = new Date(ts);
    } else {
      return "Unknown Date";
    }
    return date.toLocaleDateString(undefined, { 
      weekday: 'short',
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatTime = (ts: any) => {
    let date: Date;
    if (ts instanceof Timestamp) {
      date = ts.toDate();
    } else if (typeof ts === "number") {
      date = new Date(ts);
    } else {
      return "";
    }
    return date.toLocaleTimeString(undefined, { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatDuration = (start: any, end: any) => {
    if (!start || !end) return null;
    
    const startMs = start instanceof Timestamp ? start.toMillis() : typeof start === "number" ? start : 0;
    const endMs = end instanceof Timestamp ? end.toMillis() : typeof end === "number" ? end : 0;
    
    if (startMs === 0 || endMs === 0 || endMs <= startMs) return null;
    
    const diffMins = Math.floor((endMs - startMs) / 60000);
    
    if (diffMins < 1) return "under 1 min";
    if (diffMins < 60) {
      return `${diffMins} min`;
    }
    
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  return (
    <div className="animate-in fade-in duration-500">
      
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-24">
        
        {/* LIVE MATCH SECTION */}
        <div className="text-center mb-16">
          <h1 
            className="text-4xl sm:text-5xl font-sriracha tracking-tighter text-[#B0D18B] mb-4 opacity-0 blur-md translate-y-8"
            style={{
              animation: "fadeSlideUnblur 1s ease-out 0.2s forwards",
            }}
          >
            Live Match
          </h1>
          <p 
            className="mt-4 text-[#C4C8BA] opacity-0 blur-md translate-y-8 max-w-2xl mx-auto"
            style={{
              animation: "fadeSlideUnblur 1s ease-out 0.4s forwards",
            }}
          >
            Spectate an ongoing match by entering its code.
          </p>
          
          <div className={`mt-8 mx-auto opacity-0 blur-md translate-y-8 transition-all duration-500 ${activeCode ? 'max-w-4xl' : 'max-w-md'}`}
               style={{ animation: "fadeSlideUnblur 1s ease-out 0.5s forwards" }}>
             {!activeCode ? (
                <div className="flex gap-2">
                   <div className="relative flex-1">
                      <input 
                         type="text" 
                         value={matchCode}
                         onChange={(e) => setMatchCode(e.target.value)}
                         placeholder="Enter match code (e.g. OG1)"
                         className="w-full bg-[#1A1C16] border border-[#8E9285]/20 rounded-full py-3 px-6 pl-12 text-[#E2E3D8] placeholder-[#8E9285] focus:outline-none focus:border-[#B0D18B] focus:ring-1 focus:ring-[#B0D18B] transition-all"
                         onKeyDown={(e) => e.key === 'Enter' && handleSpectate()}
                      />
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8E9285]" />
                   </div>
                   <button 
                      onClick={handleSpectate}
                      disabled={!matchCode.trim()}
                      className="bg-[#B0D18B] hover:bg-[#9DC077] disabled:opacity-50 disabled:cursor-not-allowed text-[#1A1C16] font-bold py-3 px-6 rounded-full transition-all flex items-center gap-2"
                   >
                      <Play className="w-5 h-5 fill-current" />
                   </button>
                </div>
             ) : (
                <div className="w-full">
                   <div className="max-w-md mx-auto">
                       <div className="flex items-center justify-between bg-[#1A1C16] border border-[#B0D18B]/30 rounded-full py-2 px-4 mb-4">
                          <div className="flex items-center gap-2">
                             <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                             <span className="text-[#E2E3D8] font-bold font-mono">LIVE: {activeCode}</span>
                             <span className="text-[#8E9285] text-xs px-2 py-0.5 bg-white/5 rounded">{liveSport}</span>
                          </div>
                          <div className="flex items-center gap-1">
                              <button 
                                 onClick={toggleFullScreen}
                                 className="p-1 hover:bg-white/10 rounded-full transition-colors text-[#B0D18B] hover:text-white"
                                 title="Open in Full Screen"
                              >
                                 <Maximize2 className="w-5 h-5" />
                              </button>
                              <button 
                                 onClick={clearSpectate}
                                 className="p-1 hover:bg-white/10 rounded-full transition-colors text-[#8E9285] hover:text-white"
                                 title="Close"
                              >
                                 <X className="w-5 h-5" />
                              </button>
                          </div>
                       </div>
                   </div>
                   
                   {isLiveLoading ? (
                      <div className="flex flex-col items-center justify-center py-12 gap-4 bg-[#1A1C16] rounded-2xl border border-[#8E9285]/10 max-w-md mx-auto">
                         <Loader2 className="h-8 w-8 animate-spin text-[#B0D18B]" />
                         <p className="text-[#8E9285] text-sm animate-pulse">Connecting to scoreboard...</p>
                      </div>
                   ) : liveGameState ? (
                      <LiveScoreboard 
                         gameState={liveGameState}
                         playersData={livePlayersData}
                         settingsData={liveSettingsData}
                         sport={liveSport}
                      />
                   ) : (
                      <div className="bg-[#1A1C16] rounded-2xl p-8 border border-red-500/20 text-center max-w-md mx-auto">
                         <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                         <p className="text-red-400 mb-4">{liveError || "Match not found or waiting for data..."}</p>
                         <p className="text-[#8E9285] text-xs">Waiting for updates...</p>
                      </div>
                   )}
                </div>
             )}
          </div>
        </div>

        <div className="w-full h-px bg-[#8E9285]/10 my-16 opacity-0 blur-md translate-y-8"
             style={{ animation: "fadeSlideUnblur 1s ease-out 0.55s forwards" }} />

        {/* MATCH HISTORY SECTION */}
        <div className="text-center mb-16">
          <h1 
            className="text-4xl sm:text-5xl font-sriracha tracking-tighter text-[#B0D18B] mb-4 opacity-0 blur-md translate-y-8"
            style={{
              animation: "fadeSlideUnblur 1s ease-out 0.6s forwards",
            }}
          >
            Match History
          </h1>
          <p 
            className="mt-4 text-[#C4C8BA] opacity-0 blur-md translate-y-8 max-w-2xl mx-auto"
            style={{
              animation: "fadeSlideUnblur 1s ease-out 0.8s forwards",
            }}
          >
            Review your past performances and track your progress.
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-6">
          
          {user && (
             <div className="flex justify-center mb-8 opacity-0 blur-md translate-y-8"
                  style={{ animation: "fadeSlideUnblur 1s ease-out 0.9s forwards" }}>
                <CounterDisplay count={totalGamesCount} label="games scored this year" />
             </div>
          )}

          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <Loader2 className="h-10 w-10 animate-spin text-[#B0D18B]" />
              <p className="text-[#8E9285] animate-pulse">Fetching your latest scores...</p>
            </div>
          ) : !user ? (
            <div className="bg-[#1A1C16] border border-[#8E9285]/20 rounded-2xl p-12 text-center opacity-0 blur-md translate-y-8"
                 style={{ animation: "fadeSlideUnblur 1s ease-out 1.0s forwards" }}>
              <div className="bg-[#B0D18B]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <EmojiEventsIcon className="h-8 w-8 !fill-[#B0D18B]" />
              </div>
              <h3 className="text-[#E2E3D8] font-semibold font-sriracha text-xl mb-3">Sign in to view your scores</h3>
              <p className="text-[#C4C8BA] mb-8 max-w-sm mx-auto">
                Your match history is tied to your account. Sign in to see your win rates and past games.
              </p>
              <Link 
                href="/account"
                className="inline-block bg-[#B0D18B] hover:bg-[#9DC077] text-[#1A1C16] font-bold py-3 px-8 rounded-full transition-all shadow-lg hover:shadow-[#B0D18B]/20"
              >
                Go to Account
              </Link>
            </div>
          ) : error ? (
            <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-6 flex items-start gap-4 text-red-400">
              <AlertCircle className="h-6 w-6 shrink-0" />
              <div>
                <h4 className="font-bold mb-1">Error Loading Scores</h4>
                <p className="text-sm">{error}</p>
                <button 
                  onClick={() => fetchGames(user.uid)}
                  className="mt-4 text-xs bg-red-500/20 hover:bg-red-500/30 px-3 py-1.5 rounded-lg transition-colors border border-red-500/30"
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : games.length === 0 ? (
            <div className="bg-[#1A1C16] border border-[#8E9285]/20 rounded-2xl p-12 text-center opacity-0 blur-md translate-y-8"
                 style={{ animation: "fadeSlideUnblur 1s ease-out 1.0s forwards" }}>
              <div className="bg-[#8E9285]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <Activity className="h-8 w-8 text-[#8E9285]" />
              </div>
              <h3 className="text-[#E2E3D8] font-semibold font-sriracha text-xl mb-3">No games played yet</h3>
              <p className="text-[#C4C8BA] mb-8 max-w-sm mx-auto">
                Get out there and start scoring! Your first match results will appear here once completed.
              </p>
              <Link 
                href="/padel"
                className="inline-block bg-[#2C2E26] hover:bg-[#3C3E36] text-[#E2E3D8] font-semibold py-2 px-6 rounded-lg transition-colors border border-[#8E9285]/20"
              >
                Learn how to score
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {games.map((game, index) => (
                <div 
                  key={game.id}
                  className="bg-[#1A1C16] border border-[#8E9285]/20 rounded-2xl p-5 hover:border-[#B0D18B] transition-all duration-200 group opacity-0 blur-md translate-y-8"
                  style={{
                    animation: `fadeSlideUnblur 0.8s ease-out ${1.0 + (index * 0.1)}s forwards`,
                  }}
                >
                  <div className="flex flex-row justify-between items-start gap-2 sm:gap-4">
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className="bg-[#B0D18B]/10 p-2 sm:p-3 rounded-xl group-hover:bg-[#B0D18B]/20 transition-colors shrink-0">
                        <SportIcon sport={game.sport} />
                      </div>
                      <div className="min-w-0">
                        <h4 className="text-[#E2E3D8] font-bold font-sriracha capitalize text-base sm:text-lg leading-tight truncate">
                          {game.sport || "Match"}
                        </h4>
                        {game.locationName && (
                           <div className="text-[#8E9285] font-sans font-normal text-xs sm:text-sm truncate">
                              {game.locationName}
                           </div>
                        )}
                        <div className="flex flex-col gap-0.5 sm:gap-1 text-[10px] sm:text-xs text-[#8E9285] mt-1">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3 shrink-0" />
                            {formatDate(game.timestampStart)}
                          </span>
                          <div className="flex items-center gap-2 sm:gap-3">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3 shrink-0" />
                              {formatTime(game.timestampStart)}
                            </span>
                            {(() => {
                              const duration = formatDuration(game.timestampStart, game.timestampEnd);
                              if (duration) {
                                return (
                                  <span className="flex items-center gap-1 border-l border-[#8E9285]/20 pl-2 sm:pl-3">
                                    <Timer className="h-3 w-3 shrink-0" />
                                    {duration}
                                  </span>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-1 shrink-0 mt-0.5">
                      {game.scoreSet && (
                        <div className="bg-black/20 px-2 sm:px-3 py-0.5 sm:py-1 rounded-lg border border-[#8E9285]/10 text-[#E2E3D8] font-mono text-[10px] sm:text-sm">
                          <span className="text-[#8E9285] mr-1.5 sm:mr-2 text-[8px] sm:text-[10px] uppercase font-bold tracking-wider">Sets</span>
                          {game.scoreSet}
                        </div>
                      )}
                      {game.scoreGame && (
                        <div className="bg-black/20 px-2 sm:px-3 py-0.5 sm:py-1 rounded-lg border border-[#8E9285]/10 text-[#C4C8BA] font-mono text-[9px] sm:text-[11px]">
                          <span className="text-[#8E9285] mr-1.5 sm:mr-2 text-[8px] sm:text-[9px] uppercase font-bold tracking-wider">Games</span>
                          {game.scoreGame}
                        </div>
                      )}
                    </div>
                  </div>

                  {game.winningTeam && (
                    <div className="mt-4 pt-4 border-t border-[#8E9285]/10 grid grid-cols-2 gap-4">
                       {/* HOME Team */}
                       <div className="flex flex-col items-start gap-2">
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-bold uppercase tracking-widest ${game.winningTeam === "HOME" ? "text-[#B0D18B]" : "text-[#8E9285]"}`}>
                               Home
                            </span>
                            {game.winningTeam === "HOME" && <EmojiEventsIcon className="h-4 w-4" />}
                          </div>

                          <div className="flex flex-col gap-1 w-full">
                             {game.player2?.fldPlayerDisplayName && !game.settings?.oneVsOne && (
                                <div className="flex items-center gap-1.5 w-full">
                                   <img 
                                      src={`/drawables/numbers${game.player2.fldPlayerCourtSpot || 2}.png`} 
                                      alt="2" 
                                      className="h-3.5 w-3.5 object-contain opacity-80" 
                                   />
                                   <span className="text-[10px] sm:text-xs text-[#E2E3D8] whitespace-nowrap line-clamp-1">
                                      {(game.player2.fldPlayerDisplayName === "MVPlayer" || game.player2.fldPlayerDisplayName === "Empty slot...") ? "MVPlayer" : game.player2.fldPlayerDisplayName}
                                   </span>
                                </div>
                             )}
                             {game.player1?.fldPlayerDisplayName && (
                                <div className="flex items-center gap-1.5 w-full">
                                   <img 
                                      src={`/drawables/numbers${game.player1.fldPlayerCourtSpot || 1}.png`} 
                                      alt="1" 
                                      className="h-3.5 w-3.5 object-contain opacity-80" 
                                   />
                                   <span className="text-[10px] sm:text-xs text-[#E2E3D8] whitespace-nowrap line-clamp-1">
                                      {(game.player1.fldPlayerDisplayName === "MVPlayer" || game.player1.fldPlayerDisplayName === "Empty slot...") ? "MVPlayer" : game.player1.fldPlayerDisplayName}
                                   </span>
                                </div>
                             )}
                          </div>
                       </div>

                       {/* VISITOR Team */}
                       <div className="flex flex-col items-end gap-2 text-right">
                          <div className="flex items-center gap-2 flex-row-reverse">
                             <span className={`text-xs font-bold uppercase tracking-widest ${game.winningTeam === "VISITOR" ? "text-[#B0D18B]" : "text-[#8E9285]"}`}>
                                Visitor
                             </span>
                             {game.winningTeam === "VISITOR" && <EmojiEventsIcon className="h-4 w-4" />}
                          </div>

                          <div className="flex flex-col items-end gap-1 w-full">
                             {game.player3?.fldPlayerDisplayName && (
                                <div className="flex items-center gap-1.5 w-full justify-end">
                                   <span className="text-[10px] sm:text-xs text-[#E2E3D8] whitespace-nowrap line-clamp-1 text-right">
                                      {(game.player3.fldPlayerDisplayName === "MVPlayer" || game.player3.fldPlayerDisplayName === "Empty slot...") ? "MVPlayer" : game.player3.fldPlayerDisplayName}
                                   </span>
                                   <img 
                                      src={`/drawables/numbers${game.player3.fldPlayerCourtSpot || 3}.png`} 
                                      alt="3" 
                                      className="h-3.5 w-3.5 object-contain opacity-80" 
                                   />
                                </div>
                             )}
                             {game.player4?.fldPlayerDisplayName && !game.settings?.oneVsOne && (
                                <div className="flex items-center gap-1.5 w-full justify-end">
                                   <span className="text-[10px] sm:text-xs text-[#E2E3D8] whitespace-nowrap line-clamp-1 text-right">
                                      {(game.player4.fldPlayerDisplayName === "MVPlayer" || game.player4.fldPlayerDisplayName === "Empty slot...") ? "MVPlayer" : game.player4.fldPlayerDisplayName}
                                   </span>
                                   <img 
                                      src={`/drawables/numbers${game.player4.fldPlayerCourtSpot || 4}.png`} 
                                      alt="4" 
                                      className="h-3.5 w-3.5 object-contain opacity-80" 
                                   />
                                </div>
                             )}
                          </div>
                       </div>
                    </div>
                  )}
                </div>
              ))}

              <div className="pt-8 text-center">
                <div className="bg-[#2C2E26]/50 border border-[#8E9285]/20 rounded-2xl p-8">
                   <h4 className="text-[#E2E3D8] font-sriracha text-lg mb-2">Want deeper insights?</h4>
                   <p className="text-[#8E9285] text-sm mb-6">Detailed analytics, opponent tracking, and location history are available for premium users.</p>
                   <Link 
                    href="/store"
                    className="inline-block bg-[#B0D18B] hover:bg-[#9DC077] text-[#1A1C16] font-bold py-2.5 px-8 rounded-full transition-all text-sm shadow-lg hover:shadow-[#B0D18B]/20"
                   >
                      Activate Premium Now
                   </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
